import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';

import { SLOT_TYPES } from '../../constants';
import {
  favouritesSelector,
  favouriteTagSelector,
  tagsMappedSelector,
} from '../../selectors';
import { FETCH_TAGS_SUCCESSFUL } from '../../reducers/tags';
import { createSlotsSelector } from './selectors';

const initialState = {
  search: '',
  filters: {},
};

export default function slotsFilteredFactory(
  slotsPlacement,
  slotsSelectorWrapper,
) {
  const slotFilter = createSlice({
    name: `${slotsPlacement}Filters`,
    initialState,
    reducers: {
      setSearch(state, { payload }) {
        state.search = payload;
      },
      setFilter(state, { payload: _id }) {
        state.filters[_id] = !state.filters[_id];
      },
      resetFiltersAndSearch() {
        return initialState;
      },
    },
    extraReducers: {
      [FETCH_TAGS_SUCCESSFUL]: (state) => {
        state.filters = {};
      },
    },
  });

  const { setFilter, setSearch, resetFiltersAndSearch } = slotFilter.actions;

  const slotsFilterSelector = ({ [slotFilter.name]: slotFilters }) =>
    slotFilters;

  const slotsPlacedSelector = createSlotsSelector(slotsPlacement);

  const lootpacksSelector = createSelector(
    slotsPlacedSelector,
    tagsMappedSelector,
    (slots, tagsMap) => {
      const lootsMap = {};
      const filteredLoots = slots
        .filter(({ type }) => type === SLOT_TYPES.LOOT_SLOT)
        .flatMap(({ loots }) => loots)
        .filter(({ _id }) => {
          if (lootsMap[_id]) {
            return false;
          }
          lootsMap[_id] = true;
          return true;
        });
      return filteredLoots.map((loot) => {
        const { name, headline, subHeadline, keywords, specialKeywords, tags } =
          loot;
        const lootTagsMapped = tags
          .filter(({ hidden }) => !hidden)
          .map(({ tag, ...rest }) => ({
            ...rest,
            ...(tagsMap[tag] || {}),
            tag,
          }));

        const tagsSearchKeys = lootTagsMapped.flatMap(
          ({ keywords: tagKeywords, title }) => {
            if (!tagKeywords) {
              return null;
            }
            return [...tagKeywords, title];
          },
        );

        const searchKeys = [
          name,
          headline,
          subHeadline,
          keywords,
          specialKeywords,
          tagsSearchKeys,
        ]
          .flat()
          .filter(Boolean)
          .map((key) => key.toLowerCase());

        return { ...loot, searchKeys, tags: lootTagsMapped };
      });
    },
  );

  const selector = slotsSelectorWrapper
    ? slotsSelectorWrapper(slotsPlacedSelector)
    : slotsPlacedSelector;

  const searchedLootsSelector = createSelector(
    lootpacksSelector,
    favouritesSelector,
    favouriteTagSelector,
    slotsFilterSelector,
    (lootpacks, favourites = {}, favouriteFilter, { search, filters }) => {
      const searchInputs = search
        .split(/(\s+)/)
        .filter((x) => Boolean(x.trim()))
        .map((x) => x.toLowerCase());
      const areFiltersSelected = Object.values(filters).some(Boolean);

      let loots = lootpacks;
      if (areFiltersSelected) {
        loots = loots.filter(
          ({ tags, _id }) =>
            (filters[favouriteFilter] && favourites[_id]) ||
            tags.some(({ tag }) => filters[tag]),
        );
      }

      if (searchInputs.length) {
        loots = loots.filter(({ searchKeys }) =>
          searchInputs.every((key) =>
            // eslint-disable-next-line max-nested-callbacks
            searchKeys.some((searchKey) => searchKey.includes(key)),
          ),
        );
      }

      const searchTrimmed = search?.trim();

      return {
        loots,
        allCount: lootpacks.length,
        search,
        searchTrimmed,
        shouldShowSearchResults: searchTrimmed || areFiltersSelected,
        areFiltersSelected,
      };
    },
  );

  const useFilter = (filterId) => {
    const {
      filters: { [filterId]: checked },
    } = useSelector(slotsFilterSelector);
    const dispatch = useDispatch();
    return useMemo(
      () => [checked, () => dispatch(setFilter(filterId))],
      [checked, dispatch, filterId],
    );
  };

  const useSearch = () => {
    const { search } = useSelector(slotsFilterSelector);
    const dispatch = useDispatch();
    return useMemo(
      () => [search, (value) => dispatch(setSearch(value))],
      [dispatch, search],
    );
  };

  return {
    reducer: { [slotFilter.name]: slotFilter.reducer },
    contextFunctions: {
      useFilter,
      useSearch,
      selector,
      searchedLootsSelector,
      resetFiltersAndSearch,
    },
  };
}
